import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import Icon from "@/components/Icon";

export default function WhyUs() {
  const { content, loading } = useContent();
  if (loading || !content) return null;
  const w = content.why_us || {};

  return (
    <div data-testid="page-why-us">
      <section className="sp-hero">
        <div className="sp-container py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            {w.eyebrow && <span className="sp-eyebrow">{w.eyebrow}</span>}
            <h1 className="mt-3 text-4xl lg:text-6xl font-extrabold leading-tight">
              {w.title} <span className="text-gold">{w.title_accent}</span>
            </h1>
            {w.description && (
              <p className="mt-4 max-w-3xl text-slate-600 text-lg">{w.description}</p>
            )}
          </div>
          {w.team_image && (
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img src={w.team_image} alt="African business team" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Pillars */}
      {w.pillars?.length > 0 && (
        <section className="py-16">
          <div className="sp-container grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {w.pillars.map((p, i) => (
              <div key={p.title + i} className="sp-card h-full text-center" data-testid={`why-pillar-${i}`}>
                <div className="mx-auto w-16 h-16 rounded-full bg-navy text-gold flex items-center justify-center">
                  <Icon name={p.icon} size={28} />
                </div>
                <h3 className="mt-5 text-lg font-extrabold text-navy">{p.title}</h3>
                <div className="w-10 h-[3px] bg-gold mx-auto my-3 rounded-full" />
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Innovation showcase */}
      {w.projects?.length > 0 && (
        <section className="pb-20">
          <div className="sp-container">
            <div className="rounded-3xl p-8 lg:p-12 bg-navy text-white">
              <div className="text-center mb-10">
                <span className="sp-chip" style={{ background: "rgba(245,180,23,.18)", color: "#f5b417" }}>
                  Featured Projects
                </span>
                <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white">
                  Innovation <span className="text-gold">Showcase</span>
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
                {w.projects.map((p, i) => (
                  <div
                    key={p.title + i}
                    className="bg-white rounded-2xl p-5 text-left shadow-md hover:translate-y-[-3px] transition-transform"
                    data-testid={`project-${i}`}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                      style={{ background: `${p.color}18`, color: p.color }}
                    >
                      <Icon name={p.icon} size={22} />
                    </div>
                    <h3 className="text-base font-extrabold leading-tight" style={{ color: p.color }}>
                      {p.title}
                    </h3>
                    <p className="text-xs text-slate-600 mt-2 leading-relaxed">{p.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex justify-center">
                <Link to="/products" className="sp-btn-gold" data-testid="view-projects-cta">
                  <Icon name="Folder" size={18} /> View Projects <Icon name="ArrowRight" size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Positioning */}
      {(w.positioning_title || w.positioning_text) && (
        <section className="pb-24">
          <div className="sp-container">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-24 h-24 rounded-2xl bg-navy text-gold flex items-center justify-center">
                  <Icon name="Rocket" size={42} />
                </div>
              </div>
              <div className="lg:col-span-8 text-center lg:text-left">
                <span className="sp-eyebrow">Business Model Positioning</span>
                <h3 className="mt-2 text-2xl lg:text-3xl font-extrabold text-navy">
                  {w.positioning_title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{w.positioning_text}</p>
              </div>
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-24 h-24 rounded-2xl bg-gold/15 text-gold flex items-center justify-center">
                  <Icon name="Building2" size={42} />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
